// src/utils/labTestReminders.js
//
// In-app lab-day reminders. The backend (LabTestReminderScheduler) already
// sends WhatsApp reminders 1 day and 1 hour before a collection; this derives
// the same moments for the screens a patient actually looks at, so a reminder
// can be seen even if the WhatsApp message was missed:
//
//   - Today screen  → a row under "N items need attention"
//   - notification bell → a HIGH item
//
// Nothing is stored. Like the rest of the bell (see useNotifications.js), the
// reminder is derived at read time from the order list, so it cannot disagree
// with the order it describes and clears itself the moment the order moves on
// (sample collected, cancelled) or the day passes.
//
// Window: an order that is still ORDERED or CONFIRMED and whose collection is
// today or tomorrow (browser-local calendar days; collections are scheduled in
// IST and patients are in India). An order further out is not reminded yet.

// Previu display names, matching PanelDefinitionService in the backend. Kept
// here so the lab-tests list and the reminders name a panel identically.
export const PANEL_TIER_DISPLAY_NAMES = {
  T1: 'T1 — Essential Panel',
  T2: 'T2 — Standard Panel',
  T3: 'T3 — Precision Panel',
  ONCOVERYX: 'ONCOVERYX — Cancer Panel',
  TUMOUR_MALE: 'Tumour Panel (Male)',
  TUMOUR_FEMALE: 'Tumour Panel (Female)',
};

// Same statuses the backend's reminder scheduler considers eligible.
const REMINDER_STATUSES = ['ORDERED', 'CONFIRMED'];

const DAY_MS = 24 * 60 * 60 * 1000;

/** appointmentDate ("YYYY-MM-DD") + appointmentTime ("HH:mm" or "HH:mm:ss") → local Date, or null. */
function collectionStart(order) {
  if (!order?.appointmentDate) return null;
  const [y, m, d] = String(order.appointmentDate).split('-').map(Number);
  if (!y || !m || !d) return null;
  const [hh, mm] = String(order.appointmentTime || '').split(':');
  return new Date(y, m - 1, d, parseInt(hh, 10) || 0, parseInt(mm, 10) || 0);
}

/** The list endpoint answers { items }, { content } or { orders } depending on version. */
export function labOrdersFrom(data) {
  if (Array.isArray(data)) return data;
  return data?.items ?? data?.content ?? data?.orders ?? [];
}

/**
 * @param {object[]} orders  lab test orders for one patient
 * @param {Date}     [now]
 * @returns {{id:string, when:'today'|'tomorrow', startsAt:Date, panelName:string,
 *            timeLabel:string, title:string, meta:string, to:string}[]}
 *          soonest collection first
 */
export function labTestReminders(orders, now = new Date()) {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return (Array.isArray(orders) ? orders : [])
    .filter(o => REMINDER_STATUSES.includes(String(o?.status || '').toUpperCase()))
    .map(o => {
      const startsAt = collectionStart(o);
      if (!startsAt) return null;
      const day = new Date(startsAt.getFullYear(), startsAt.getMonth(), startsAt.getDate());
      const dayDiff = Math.round((day - startOfToday) / DAY_MS);
      if (dayDiff < 0 || dayDiff > 1) return null;
      const when = dayDiff === 0 ? 'today' : 'tomorrow';
      const panelName = PANEL_TIER_DISPLAY_NAMES[o.panelTier]
        || o.plan || o.testCode || 'Lab test';
      const timeLabel = startsAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      return {
        id: o.id,
        when,
        startsAt,
        panelName,
        timeLabel,
        title: when === 'today' ? 'Lab test collection today' : 'Lab test tomorrow',
        meta: `${panelName} · ${when === 'today' ? 'today' : 'tomorrow'} at ${timeLabel}`,
        to: `/patient/lab-tests/${o.id}`,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startsAt - b.startsAt);
}
